import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import googleDriveService from '../../services/googleDrive';
import { generateDocumentContent } from '../../templates/technicalDocumentTemplate';

const TechnicalDocumentModal = ({ isOpen, onClose, currentFolder, onDocumentCreated }) => {
  const [documentTitle, setDocumentTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDocumentTitle('');
      setError(null);
      // Add body class to prevent scrolling
      document.body.classList.add('modal-open');
    } else {
      // Remove body class to restore scrolling
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleCreateDocument = async () => {
    if (!documentTitle.trim()) {
      setError('Document title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await googleDriveService.getAccessToken();
      
      // Generate the document content from template
      const content = generateDocumentContent(documentTitle.trim());
      
      // Create the file metadata
      const metadata = {
        name: `${documentTitle.trim().toLowerCase().replace(/\s+/g, '-')}-documentation.md`,
        parents: [currentFolder?.id]
      };

      console.log('Creating technical document:', metadata);

      // Create the file with content using multipart upload
      const boundary = '-------314159265358979323846';
      const delimiter = "\r\n--" + boundary + "\r\n";
      const close_delim = "\r\n--" + boundary + "--";

      const multipartRequestBody =
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: text/markdown\r\n\r\n' +
        content +
        close_delim;

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'multipart/related; boundary="' + boundary + '"'
        },
        body: multipartRequestBody
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Document creation failed:', errorText);
        throw new Error('Failed to create technical document');
      }

      const document = await response.json();
      console.log('✅ Technical document created successfully:', document);

      // Add permissions for domain
      try {
        await fetch(`https://www.googleapis.com/drive/v3/files/${document.id}/permissions?supportsAllDrives=true`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: 'writer',
            type: 'domain',
            domain: 'drilldocs.com',
            allowFileDiscovery: true
          })
        });
      } catch (err) {
        console.warn('Failed to set permissions:', err);
      }

      onDocumentCreated(document);
      onClose();
      
      // Refresh to show new document
      window.location.reload();

    } catch (error) {
      console.error('Failed to create technical document:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateDocument();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay">
      <div className="document-creation-modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>Create Technical Document</h2>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="doc-title">System/Service Name</label>
            <input
              id="doc-title"
              type="text"
              className="form-input"
              placeholder="e.g., Grafana Monitoring Solution"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={loading}
              autoFocus
            />
            <small className="form-help">This will be the main title of your technical documentation</small>
          </div>


          {currentFolder && (
            <div className="form-group">
              <label>Location</label>
              <div className="location-info">
                📁 {currentFolder.name}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateDocument}
            disabled={loading || !documentTitle.trim()}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Creating...
              </>
            ) : (
              'Create Technical Document'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TechnicalDocumentModal;